import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import OrdersDetail from "@/pages/Orders/OrdersDetail"
import stores from "@/stores"

import styles from "./ShipmentDrawer.module.scss"

const ANIMATION_DURATION = 220

interface ShipmentNavState {
  query?: string
  orders?: string
  orderIndex?: number | string
  orderName?: string
}

export function ShipmentDrawer() {
  const { shipmentID } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const navState = (location.state as ShipmentNavState | null) || null

  // open 控制进入 / 退出动画的 transform 状态
  const [open, setOpen] = useState(false)
  const closeTimerRef = useRef<number | null>(null)

  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true))

    return () => {
      cancelAnimationFrame(id)
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  const handleClose = () => {
    setOpen(false)
    const ordersQuery = navState?.query ?? ""
    const target = "/orders" + decodeURIComponent(ordersQuery)

    closeTimerRef.current = window.setTimeout(() => navigate(target), ANIMATION_DURATION)
  }

  // ESC 关闭
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose()
      }
    }

    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  // 抽屉打开时禁用 body 滚动
  useEffect(() => {
    const originalOverflow = document.body.style.overflow

    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [])

  // 通知 Orders 列表关闭其 J/K 加速键，避免与抽屉内 OrdersDetail 的 J/K 翻页冲突
  useEffect(() => {
    stores.ordersStore.setShipmentDrawerOpen(true)
    return () => {
      stores.ordersStore.setShipmentDrawerOpen(false)
    }
  }, [])

  if (!shipmentID) {
    return null
  }

  return createPortal(
    <>
      <div
        className={`${styles.Backdrop} ${open ? styles.Open : ""}`}
        onClick={handleClose}
        style={{ zIndex: 299 }}
      />
      <aside
        className={`${styles.Drawer} ${open ? styles.Open : ""}`}
        role="dialog"
        aria-modal="true"
        style={{ zIndex: 299 }}
      >
        <div className={styles.Content}>
          <OrdersDetail
            numberID={shipmentID}
            onClose={handleClose}
          />
        </div>
      </aside>
    </>,
    document.body,
  )
}

export default ShipmentDrawer
